import React, { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator, Download, Save, Trash2,
  Plane, BarChart3,
  CheckCircle2, RefreshCw,
  ChevronDown, Star, Building2, DollarSign, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { pricingApi } from "@/api";
import { exportPricingReport } from "@/utils/exportUtils";
import { toast } from "sonner";

// ── Preset airline profiles ────────────────────────────────────────────────
const PRESETS = {
  akasa: {
    airline_name: "Sample Airline",
    fleet_size: 37, seats_per_aircraft: 189, sectors_per_day: 4,
    load_factor: 82, avg_fare: 4500, subscription_per_aircraft: 40000, currency: "INR",
  },
  indigo: {
    airline_name: "IndiGo",
    fleet_size: 340, seats_per_aircraft: 180, sectors_per_day: 4,
    load_factor: 86, avg_fare: 4200, subscription_per_aircraft: 25000, currency: "INR",
  },
  yeti: {
    airline_name: "Yeti Airlines",
    fleet_size: 16, seats_per_aircraft: 78, sectors_per_day: 4,
    load_factor: 75, avg_fare: 5800, subscription_per_aircraft: 40000, currency: "INR",
  },
  air_india: {
    airline_name: "Air India",
    fleet_size: 115, seats_per_aircraft: 220, sectors_per_day: 3,
    load_factor: 79, avg_fare: 6200, subscription_per_aircraft: 28000, currency: "INR",
  },
};

const DEFAULT_INPUTS = { ...PRESETS.akasa, name: "Sample Base Case" };

// ── Formula engine ──────────────────────────────────────────────────────────
// Based on: 1 A/C × sectors/day × (seats × LF) = revenue seats/A/C/day
//           × fare = revenue/A/C/day  ×  fleet = daily fleet revenue  ×  365 = annual
function computeMetrics(inp) {
  const {
    fleet_size: F, seats_per_aircraft: S, sectors_per_day: SPD,
    load_factor: LF, avg_fare: FARE, subscription_per_aircraft: SPA,
  } = inp;
  if (!F || !S || !SPD || !FARE || !SPA) return null;

  const lf = LF / 100;

  // Step 1: revenue seats per aircraft per day
  const revenueSeatsPerAcPerDay   = SPD * S * lf;            // e.g. 4 × 70 × 0.8 = 224
  // Step 2: revenue per aircraft per day
  const revenuePerAcPerDay        = revenueSeatsPerAcPerDay * FARE;
  // Step 3: daily fleet revenue
  const dailyFleetRevenue         = revenuePerAcPerDay * F;
  // Step 4: annual fleet revenue
  const annualFleetRevenue        = dailyFleetRevenue * 365;

  // Platform cost
  const monthlyFleetCost          = SPA * F;
  const annualFleetCost           = SPA * F * 12;
  const annualCostPerAc           = SPA * 12;

  // PRIMARY: cost per revenue seat (annual basis)
  // = annual cost per A/C ÷ (sectors/day × seats × LF × 365)
  const annualRevSeatsPerAc       = SPD * S * lf * 365;
  const costPerRevenueSeat        = annualCostPerAc / annualRevSeatsPerAc;

  // PRIMARY: platform cost as % of annual revenue
  const pctOfRevenue              = (annualFleetCost / annualFleetRevenue) * 100;

  // CONTEXT
  const costPerSector             = monthlyFleetCost / (F * SPD * 30);

  return {
    revenueSeatsPerAcPerDay, revenuePerAcPerDay,
    dailyFleetRevenue, annualFleetRevenue,
    monthlyFleetCost, annualFleetCost,
    costPerRevenueSeat, pctOfRevenue, costPerSector,
  };
}

// ── Formatting helpers ──────────────────────────────────────────────────────
function fmt(val, cur = "INR", dp = 0) {
  if (cur === "INR") {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
    if (val >= 100000)   return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000)     return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${val.toFixed(dp)}`;
  }
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000)    return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(dp)}`;
}

function fmtSmall(val, cur = "INR") {
  if (cur === "INR") return `₹${val.toFixed(3)}`;
  return `$${val.toFixed(4)}`;
}

function numFmt(n) { return n.toLocaleString("en-IN"); }

const SEV_COLORS = {
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", glow: "#10b981" },
  blue:    { bg: "bg-blue-500/10",    border: "border-blue-500/30",    text: "text-blue-400",    glow: "#3b82f6" },
  amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/30",   text: "text-amber-400",   glow: "#f59e0b" },
  orange:  { bg: "bg-orange-500/10",  border: "border-orange-500/30",  text: "text-orange-400",  glow: "#f97316" },
  red:     { bg: "bg-red-500/10",     border: "border-red-500/30",     text: "text-red-400",     glow: "#ef4444" },
};

// ── Numeric Input ──────────────────────────────────────────────────────────
function NumInput({ label, value, onChange, min = 0, max, step = 1, suffix = "", hint }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-muted-foreground/70 uppercase tracking-widest mb-1">
        {label}
      </label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="flex-1 bg-background border border-border rounded-sm px-2.5 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-primary transition-colors"
        />
        {suffix && <span className="text-[11px] text-muted-foreground w-8 shrink-0">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-muted-foreground/50 mt-0.5">{hint}</p>}
    </div>
  );
}

// ── Metric Card ─────────────────────────────────────────────────────────────
function MetCard({ label, value, sub, icon: Icon, color = "default", large, testid }) {
  const cls = color !== "default" ? SEV_COLORS[color] : { bg: "bg-card", border: "border-border", text: "text-primary" };
  return (
    <div
      className={`rounded-sm border p-3 ${large ? "p-4" : ""} ${cls.bg} ${cls.border}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 mb-1">{label}</p>
          <p className={`font-black font-mono ${large ? "text-3xl" : "text-xl"} ${cls.text}`}>{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${cls.bg} border ${cls.border}`}>
            <Icon size={13} className={cls.text} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function PricingCalculator() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState("");
  const [activeTab, setActiveTab] = useState("calculator");
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(84); // 1 USD = ₹84

  const metrics = useMemo(() => computeMetrics(inputs), [inputs]);

  const set = useCallback((key, val) => setInputs(prev => ({ ...prev, [key]: val })), []);

  // Auto-convert monetary inputs when switching currency
  const handleCurrencySwitch = useCallback((newCur) => {
    setInputs(prev => {
      if (prev.currency === newCur) return prev;
      const convert = (v) =>
        newCur === "USD" ? Math.round((v / exchangeRate) * 100) / 100
                         : Math.round(v * exchangeRate);
      return {
        ...prev,
        currency: newCur,
        avg_fare: convert(prev.avg_fare),
        subscription_per_aircraft: convert(prev.subscription_per_aircraft),
      };
    });
  }, [exchangeRate]);

  // Load scenarios from backend
  const loadScenarios = useCallback(async () => {
    setLoadingScenarios(true);
    try {
      const res = await pricingApi.listScenarios();
      setScenarios(res.data || []);
    } catch {
      toast.error("Failed to load scenarios");
    } finally {
      setLoadingScenarios(false);
    }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const applyPreset = useCallback((key) => {
    setInputs(prev => ({ ...prev, ...PRESETS[key], name: PRESETS[key].airline_name + " Base Case" }));
    setShowPresets(false);
    toast.success(`Loaded ${PRESETS[key].airline_name} profile`);
  }, []);

  const saveScenario = useCallback(async () => {
    if (!scenarioName.trim()) { toast.error("Enter a scenario name"); return; }
    setSaving(true);
    try {
      const payload = { ...inputs, name: scenarioName };
      await pricingApi.createScenario(payload);
      toast.success("Scenario saved");
      setScenarioName("");
      await loadScenarios();
    } catch {
      toast.error("Failed to save scenario");
    } finally {
      setSaving(false);
    }
  }, [inputs, scenarioName, loadScenarios]);

  const deleteScenario = useCallback(async (id) => {
    await pricingApi.deleteScenario(id);
    setScenarios(prev => prev.filter(s => s.id !== id));
    setSelectedScenarios(prev => prev.filter(sid => sid !== id));
    toast.success("Scenario deleted");
  }, []);

  const loadScenarioToCalculator = useCallback((s) => {
    setInputs({ ...s });
    setActiveTab("calculator");
    toast.success(`Loaded "${s.name}"`);
  }, []);

  const toggleSelectScenario = useCallback((id) => {
    setSelectedScenarios(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const selected = selectedScenarios.length > 0
        ? scenarios.filter(s => selectedScenarios.includes(s.id))
        : [];
      exportPricingReport({ inputs, metrics, scenarios: selected, currency: inputs.currency, exchangeRate });
      toast.success("PDF report generated");
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setExporting(false);
    }
  }, [inputs, metrics, scenarios, selectedScenarios]);

  // Build comparison data for chart
  const comparisonData = useMemo(() => {
    const toCompare = selectedScenarios.length > 0
      ? scenarios.filter(s => selectedScenarios.includes(s.id))
      : [];
    return toCompare.map(s => {
      const m = computeMetrics(s);
      if (!m) return null;
      return {
        name: s.name,
        "Cost/Rev Seat": parseFloat(m.costPerRevenueSeat.toFixed(3)),
        "% of Revenue": parseFloat(m.pctOfRevenue.toFixed(4)),
      };
    }).filter(Boolean);
  }, [scenarios, selectedScenarios]);

  // severity colors removed

  return (
    <div className="flex flex-col h-full bg-background" data-testid="pricing-calculator-page">
      {/* ── Header ── */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Calculator size={15} className="text-primary" />
          </div>
          <div>
            <h1 className="text-sm font-black text-foreground tracking-tight">Pricing Impact Calculator</h1>
            <p className="text-[11px] text-muted-foreground">Translate platform cost into operational metrics for sales discussions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Tab switcher */}
          <div className="flex bg-card border border-border rounded-sm p-0.5">
            {["calculator", "scenarios"].map(t => (
              <button key={t} onClick={() => setActiveTab(t)} data-testid={`tab-${t}`}
                className={`px-3 py-1 text-xs font-bold rounded-[2px] transition-colors capitalize ${
                  activeTab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
                {t === "calculator" ? "Calculator" : `Scenarios (${scenarios.length})`}
              </button>
            ))}
          </div>
          <button onClick={handleExport} disabled={exporting || !metrics}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/20 text-primary text-xs font-bold rounded-sm hover:bg-primary/20 transition-colors disabled:opacity-40"
            data-testid="export-pdf-btn">
            <Download size={12} />
            {exporting ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {/* ─── CALCULATOR TAB ─── */}
        {activeTab === "calculator" && (
          <div className="flex h-full">
            {/* ── Left: Inputs ── */}
            <div className="w-72 shrink-0 border-r border-border flex flex-col overflow-y-auto" data-testid="inputs-panel">
              {/* Presets bar */}
              <div className="px-4 pt-4 pb-3 border-b border-border/60">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest">Quick Load</span>
                  <button onClick={() => setShowPresets(!showPresets)}
                    className="text-[10px] text-primary flex items-center gap-0.5"
                    data-testid="presets-toggle">
                    Presets <ChevronDown size={10} className={showPresets ? "rotate-180" : ""} />
                  </button>
                </div>
                <AnimatePresence>
                  {showPresets && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }} className="grid grid-cols-2 gap-1.5">
                      {Object.entries(PRESETS).map(([key, p]) => (
                        <button key={key} onClick={() => applyPreset(key)} data-testid={`preset-${key}`}
                          className="px-2 py-1.5 bg-card border border-border rounded-sm text-[10px] font-bold text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors text-left truncate">
                          {p.airline_name}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex-1 px-4 py-4 space-y-5 overflow-y-auto">
                {/* Airline Profile */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Building2 size={11} className="text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Airline Profile</span>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className="block text-[10px] font-bold text-muted-foreground/70 uppercase tracking-widest mb-1">Airline Name</label>
                      <input value={inputs.airline_name} onChange={e => set("airline_name", e.target.value)}
                        className="w-full bg-background border border-border rounded-sm px-2.5 py-1.5 text-sm font-medium text-foreground focus:outline-none focus:border-primary transition-colors"
                        data-testid="input-airline-name" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-muted-foreground/70 uppercase tracking-widest mb-1">Currency</label>
                      <div className="flex gap-1.5 mb-2">
                        {["INR", "USD"].map(c => (
                          <button key={c} onClick={() => handleCurrencySwitch(c)} data-testid={`currency-${c}`}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-sm border transition-colors ${
                              inputs.currency === c ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"
                            }`}>
                            {c === "INR" ? "₹ INR" : "$ USD"}
                          </button>
                        ))}
                      </div>
                      {inputs.currency === "USD" && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap">1 USD =</span>
                          <input
                            type="number"
                            value={exchangeRate}
                            min={1}
                            step={0.5}
                            onChange={e => setExchangeRate(parseFloat(e.target.value) || 84)}
                            className="w-20 bg-background border border-border rounded-sm px-2 py-1 text-xs font-mono text-foreground focus:outline-none focus:border-primary transition-colors"
                            data-testid="exchange-rate-input"
                          />
                          <span className="text-[10px] text-muted-foreground/60">₹</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fleet Configuration */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Plane size={11} className="text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Fleet & Operations</span>
                  </div>
                  <div className="space-y-2.5">
                    <NumInput label="Fleet Size" value={inputs.fleet_size} onChange={v => set("fleet_size", v)} min={1} suffix="A/C" hint="Total operating aircraft" />
                    <NumInput label="Seats / Aircraft" value={inputs.seats_per_aircraft} onChange={v => set("seats_per_aircraft", v)} min={1} suffix="seats" />
                    <NumInput label="Sectors / Day / Aircraft" value={inputs.sectors_per_day} onChange={v => set("sectors_per_day", v)} min={1} step={1} suffix="/day" hint="e.g. 4 sectors/day = 28/week" />
                    <NumInput label="Load Factor" value={inputs.load_factor} onChange={v => set("load_factor", Math.min(100, v))} min={1} max={100} suffix="%" hint="Avg seat fill %" />
                  </div>
                </div>

                {/* Financial Parameters */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <DollarSign size={11} className="text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Financial Parameters</span>
                  </div>
                  <div className="space-y-2.5">
                    <NumInput label="Avg Fare / Sector" value={inputs.avg_fare}
                      onChange={v => set("avg_fare", v)} min={1}
                      suffix={inputs.currency === "INR" ? "₹" : "$"}
                      hint="All-in average revenue per booked seat" />
                    <NumInput label="Subscription per Aircraft / Month" value={inputs.subscription_per_aircraft}
                      onChange={v => set("subscription_per_aircraft", v)} min={1}
                      suffix={inputs.currency === "INR" ? "₹" : "$"}
                      hint={`Total fleet cost: ${metrics ? fmt(metrics.monthlyFleetCost, inputs.currency) : "—"} / month`} />
                  </div>
                </div>
              </div>

              {/* Save scenario panel */}
              <div className="border-t border-border px-4 py-3 shrink-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">Save as Scenario</p>
                <div className="flex gap-1.5">
                  <input
                    value={scenarioName}
                    onChange={e => setScenarioName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && saveScenario()}
                    placeholder="Scenario name…"
                    className="flex-1 bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:border-primary transition-colors"
                    data-testid="scenario-name-input"
                  />
                  <button onClick={saveScenario} disabled={saving || !scenarioName.trim()}
                    className="px-2.5 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-40"
                    data-testid="save-scenario-btn">
                    <Save size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Right: Live Metrics ── */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="metrics-panel">
              {metrics ? (
                <>
                  {/* ── Hero KPI Row ── */}
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div key={metrics.costPerRevenueSeat.toFixed(4)}
                      initial={{ opacity: 0.5, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                      className="col-span-1 rounded-sm border border-border bg-card p-5"
                      data-testid="metric-cost-per-revenue-seat">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-2">Cost per Revenue Seat</p>
                      <p className="text-5xl font-black font-mono text-foreground">
                        {fmtSmall(metrics.costPerRevenueSeat, inputs.currency)}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Annual subscription per A/C ÷ ({inputs.sectors_per_day} sectors × {inputs.seats_per_aircraft} seats × {inputs.load_factor}% × 365 days)
                      </p>
                    </motion.div>

                    <motion.div key={metrics.pctOfRevenue.toFixed(6)}
                      initial={{ opacity: 0.5, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                      className="col-span-1 rounded-sm border border-border bg-card p-5"
                      data-testid="metric-pct-revenue">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-2">Platform Cost as % of Annual Revenue</p>
                      <p className="text-5xl font-black font-mono text-foreground">
                        {metrics.pctOfRevenue.toFixed(4)}%
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Annual fleet cost {fmt(metrics.annualFleetCost, inputs.currency)} vs annual revenue {fmt(metrics.annualFleetRevenue, inputs.currency)}
                      </p>
                    </motion.div>
                  </div>

                  {/* ── Operational Context ── */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5">Revenue Build-Up</p>
                    <div className="grid grid-cols-4 gap-3">
                      <MetCard label="Rev Seats / A/C / Day"
                        value={Math.round(metrics.revenueSeatsPerAcPerDay).toLocaleString()}
                        sub={`${inputs.sectors_per_day} sectors × ${inputs.seats_per_aircraft} seats × ${inputs.load_factor}%`}
                        icon={Plane} testid="metric-rev-seats-ac-day" />
                      <MetCard label="Revenue / A/C / Day"
                        value={fmt(metrics.revenuePerAcPerDay, inputs.currency)}
                        sub={`${Math.round(metrics.revenueSeatsPerAcPerDay)} seats × ${fmt(inputs.avg_fare, inputs.currency)}`}
                        icon={DollarSign} testid="metric-rev-ac-day" />
                      <MetCard label="Daily Fleet Revenue"
                        value={fmt(metrics.dailyFleetRevenue, inputs.currency)}
                        sub={`${inputs.fleet_size} aircraft × day`}
                        icon={TrendingUp} testid="metric-daily-fleet-rev" />
                      <MetCard label="Annual Fleet Revenue"
                        value={fmt(metrics.annualFleetRevenue, inputs.currency)}
                        sub={`Daily × 365`}
                        icon={BarChart3} testid="metric-annual-revenue" />
                    </div>
                  </div>

                  {/* ── Cost Context ── */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5">Platform Cost Context</p>
                    <div className="grid grid-cols-3 gap-3">
                      <MetCard label="Monthly Fleet Cost"
                        value={fmt(metrics.monthlyFleetCost, inputs.currency)}
                        sub={`${inputs.fleet_size} A/C × ${fmt(inputs.subscription_per_aircraft, inputs.currency)}`}
                        icon={Calculator} testid="metric-monthly-cost" />
                      <MetCard label="Annual Fleet Cost"
                        value={fmt(metrics.annualFleetCost, inputs.currency)}
                        sub="Monthly × 12"
                        icon={Calculator} testid="metric-annual-cost" />
                      <MetCard label="Cost per Sector"
                        value={fmtSmall(metrics.costPerSector, inputs.currency)}
                        sub="Per operated flight"
                        icon={Plane} testid="metric-cost-per-sector" />
                    </div>
                  </div>

                  {/* ── ROI Section ── REMOVED */}

                  {/* ── Sales Talking Points ── */}
                  <div className="bg-card border border-border rounded-sm p-4" data-testid="talking-points">
                    <div className="flex items-center gap-2 mb-3">
                      <Star size={12} className="text-amber-400" />
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Sales Talking Points</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        {
                          label: "Per-aircraft framing",
                          value: `At ${fmt(inputs.subscription_per_aircraft, inputs.currency)} per aircraft per month, Fatigue Shield costs less than a single crew hotel night — for a platform that protects every tail in your fleet.`,
                        },
                        {
                          label: "Revenue framing",
                          value: `Platform cost is only ${metrics.pctOfRevenue.toFixed(4)}% of your annual revenue of ${fmt(metrics.annualFleetRevenue, inputs.currency)} — a rounding error against the regulatory risk it eliminates.`,
                        },
                        {
                          label: "Passenger framing",
                          value: `Each booked ticket carries just ${fmtSmall(metrics.costPerRevenueSeat, inputs.currency)} of fatigue-management overhead — across ${Math.round(metrics.revenueSeatsPerAcPerDay).toLocaleString()} revenue seats per aircraft per day.`,
                        },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-background border border-border/50 rounded-sm p-3">
                          <p className="text-[10px] font-black text-primary uppercase tracking-widest mb-1">{label}</p>
                          <p className="text-[11px] text-muted-foreground leading-relaxed">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  Enter valid inputs to see metrics
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── SCENARIOS TAB ─── */}
        {activeTab === "scenarios" && (
          <div className="p-6 space-y-5 overflow-y-auto h-full" data-testid="scenarios-panel">
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-black text-foreground">Saved Scenarios</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Select up to 3 scenarios to compare side-by-side. Click a row to load it in the calculator.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedScenarios.length > 0 && (
                  <span className="text-[10px] font-bold text-primary bg-primary/10 border border-primary/20 rounded-sm px-2 py-0.5">
                    {selectedScenarios.length} selected for comparison
                  </span>
                )}
                <button onClick={loadScenarios} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="refresh-scenarios">
                  <RefreshCw size={13} className={loadingScenarios ? "animate-spin" : ""} />
                </button>
              </div>
            </div>

            {/* Scenarios table */}
            {scenarios.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Calculator size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No scenarios saved yet</p>
                <p className="text-xs mt-1">Go to the Calculator tab and save a scenario to compare later.</p>
              </div>
            ) : (
                <div className="border border-border rounded-sm overflow-hidden" data-testid="scenarios-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-card border-b border-border">
                      <th className="w-8 px-3 py-2.5 text-left"></th>
                      <th className="px-3 py-2.5 text-left font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Name</th>
                      <th className="px-3 py-2.5 text-left font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Airline</th>
                      <th className="px-3 py-2.5 text-right font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Fleet</th>
                      <th className="px-3 py-2.5 text-right font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Monthly Cost</th>
                      <th className="px-3 py-2.5 text-right font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Subscription / A/C</th>
                      <th className="px-3 py-2.5 text-right font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Cost / Rev Seat</th>
                      <th className="px-3 py-2.5 text-right font-bold text-muted-foreground uppercase tracking-widest text-[10px]">% of Annual Rev</th>
                      <th className="px-2 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarios.map((s, i) => {
                      const m = computeMetrics(s);
                      const sc = SEV_COLORS.blue; // severity removed — use neutral
                      const isSelected = selectedScenarios.includes(s.id);
                      return (
                        <tr key={s.id}
                          className={`border-b border-border/50 hover:bg-card/60 transition-colors cursor-pointer ${isSelected ? "bg-primary/5" : ""}`}
                          onClick={() => loadScenarioToCalculator(s)}
                          data-testid={`scenario-row-${i}`}
                        >
                          <td className="px-3 py-2.5" onClick={e => { e.stopPropagation(); toggleSelectScenario(s.id); }}>
                            <div className={`w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${isSelected ? "bg-primary border-primary" : "border-border"}`}>
                              {isSelected && <CheckCircle2 size={10} className="text-primary-foreground" />}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-bold text-foreground">{s.name}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{s.airline_name}</td>
                          <td className="px-3 py-2.5 text-right text-muted-foreground">{s.fleet_size} A/C</td>
                          <td className="px-3 py-2.5 text-right font-mono text-foreground">{fmt(s.subscription_per_aircraft || 0, s.currency)}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-bold text-primary">
                            {m ? fmtSmall(m.costPerRevenueSeat, s.currency) : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">
                            {m ? `${m.pctOfRevenue.toFixed(4)}%` : "—"}
                          </td>
                          <td className="px-2 py-2.5" onClick={e => { e.stopPropagation(); deleteScenario(s.id); }}>
                            <button className="p-1 text-muted-foreground/40 hover:text-destructive transition-colors" data-testid={`delete-scenario-${i}`}>
                              <Trash2 size={11} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Side-by-side comparison when scenarios are selected */}
            {selectedScenarios.length >= 2 && comparisonData.length >= 2 && (
              <div data-testid="comparison-section">
                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-3">
                  Side-by-Side Comparison
                </p>
                {/* Comparison metric grid */}
                <div className="overflow-x-auto mb-5">
                  <table className="text-xs border border-border rounded-sm overflow-hidden w-full">
                    <thead>
                      <tr className="bg-card border-b border-border">
                        <th className="px-4 py-2.5 text-left font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Metric</th>
                        {scenarios.filter(s => selectedScenarios.includes(s.id)).map(s => (
                          <th key={s.id} className="px-4 py-2.5 text-right font-bold text-primary text-[10px]">{s.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "Airline",                fn: (s) => s.airline_name },
                        { label: "Fleet Size",             fn: (s) => `${s.fleet_size} A/C` },
                        { label: "Sectors / Day",          fn: (s) => `${s.sectors_per_day}` },
                        { label: "Subscription / A/C",     fn: (s) => fmt(s.subscription_per_aircraft || 0, s.currency) },
                        { label: "Cost / Revenue Seat",    fn: (s, m) => m ? fmtSmall(m.costPerRevenueSeat, s.currency) : "—" },
                        { label: "% of Annual Revenue",    fn: (s, m) => m ? `${m.pctOfRevenue.toFixed(4)}%` : "—" },
                        { label: "Rev Seats / A/C / Day",  fn: (s, m) => m ? Math.round(m.revenueSeatsPerAcPerDay).toLocaleString() : "—" },
                        { label: "Daily Fleet Revenue",    fn: (s, m) => m ? fmt(m.dailyFleetRevenue, s.currency) : "—" },
                        { label: "Annual Fleet Revenue",   fn: (s, m) => m ? fmt(m.annualFleetRevenue, s.currency) : "—" },
                        { label: "Annual Fleet Cost",      fn: (s, m) => m ? fmt(m.annualFleetCost, s.currency) : "—" },
                      ].map(({ label, fn }, ri) => {
                        const selS = scenarios.filter(s => selectedScenarios.includes(s.id));
                        const vals = selS.map(s => fn(s, computeMetrics(s)));
                        return (
                          <tr key={label} className={`border-b border-border/50 ${ri % 2 === 0 ? "bg-background" : "bg-card/30"}`}>
                            <td className="px-4 py-2 font-bold text-muted-foreground">{label}</td>
                            {selS.map(s => {
                              const v = fn(s, computeMetrics(s));
                              return (
                                <td key={s.id} className="px-4 py-2 text-right font-mono text-foreground">{v}</td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bar chart */}
                <div className="bg-card border border-border rounded-sm p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-4">% Impact on Revenue — Comparison</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={comparisonData} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={v => `${v}%`} />
                      <Tooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 2, fontSize: 11 }}
                        formatter={(v) => [`${v}%`, "% Impact on Revenue"]}
                      />
                      <Bar dataKey="% of Revenue" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
